/**
 * STEP — resilient lazy() for code-split routes.
 *
 * A bare `lazy(() => import(...))` fails hard on a transient chunk-load error —
 * a network blip, or (most commonly) a stale chunk hash right after a deploy —
 * leaving the user on a white screen until they manually reload. This wraps the
 * dynamic import so it:
 *   1. retries a few times with backoff (rides out momentary network failures), then
 *   2. on final failure does a ONE-TIME full reload (guarded against loops) to
 *      pull the fresh index.html + current chunks.
 *
 * Use exactly like React.lazy: `const Page = lazyRetry(() => import("@/pages/…"))`.
 */
import { lazy, type ComponentType } from "react";

type Loader = () => Promise<{ default: ComponentType<any> }>;

const RELOAD_KEY = "step_chunk_reload_ts";
const RELOAD_COOLDOWN_MS = 12_000; // never reload-loop faster than this

function retryImport(factory: Loader, retries = 3, delay = 350): Promise<{ default: ComponentType<any> }> {
  return factory().catch((err: unknown) => {
    if (retries > 0) {
      return new Promise<void>((res) => setTimeout(res, delay)).then(() =>
        retryImport(factory, retries - 1, Math.round(delay * 1.6)),
      );
    }
    // Out of retries. A removed/renamed chunk after a deploy is the usual cause;
    // one hard reload fetches the new build. Guard so we can never loop forever.
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last > RELOAD_COOLDOWN_MS) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
        // Leave the promise pending — the reload is already in flight.
        return new Promise<{ default: ComponentType<any> }>(() => {});
      }
    } catch {
      /* sessionStorage may be unavailable (private mode); fall through to throw */
    }
    throw err;
  });
}

export function lazyRetry(factory: Loader) {
  return lazy(() => retryImport(factory));
}
